import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Users, CheckCircle, Clock, Search, UserCheck, Wifi, RefreshCw } from 'lucide-react';
import { adminAPI } from '../lib/api';
import { PageHeader, LoadingPage, StatCard } from '../components/shared';
import toast from 'react-hot-toast';

const REFRESH_INTERVAL_MS = 15000;

export default function AdminCheckinPage() {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [filter, setFilter] = useState('all'); // all | checked | pending
  const [search, setSearch] = useState('');
  const [checkingIn, setCheckingIn] = useState({});
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const intervalRef = useRef(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const r = await adminAPI.getEventRegistrations();
      setSummary(r.data.summary);
      setRegistrations(r.data.registrations || []);
      setLastRefreshed(new Date());
    } catch {
      if (!silent) toast.error('Failed to load registrations.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    intervalRef.current = setInterval(() => load(true), REFRESH_INTERVAL_MS);
    return () => clearInterval(intervalRef.current);
  }, [load]);

  const handleCheckin = async (reg) => {
    const reference = 'FIA-' + reg.id.slice(0, 8).toUpperCase();
    setCheckingIn(c => ({ ...c, [reg.id]: true }));
    try {
      await adminAPI.confirmCheckin(reference);
      toast.success(`${reg.full_name} checked in.`);
      load(true);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Check-in failed.');
    } finally {
      setCheckingIn(c => ({ ...c, [reg.id]: false }));
    }
  };

  if (loading) return <LoadingPage />;

  const term = search.trim().toLowerCase();
  const visible = registrations.filter(r => {
    if (filter === 'checked' && !r.checked_in) return false;
    if (filter === 'pending' && r.checked_in) return false;
    if (term && !r.full_name.toLowerCase().includes(term) && !r.email.toLowerCase().includes(term)) return false;
    return true;
  });

  const pctChecked = summary?.total ? Math.round((summary.checked_in / summary.total) * 100) : 0;

  return (
    <div>
      <PageHeader
        eyebrow="Grand Finale"
        title="Event Check-In"
        subtitle="Live view of registrations and arrivals"
        actions={
          <button className="btn btn-ghost btn-sm" onClick={() => load()}>
            <RefreshCw size={14} /> Refresh
          </button>
        }
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 24 }}>
        <StatCard label="Registered" value={summary?.total ?? 0} icon={Users} color="teal" />
        <StatCard label="Checked In" value={summary?.checked_in ?? 0} icon={UserCheck} color="gold" sub={`${pctChecked}% of registered`} />
        <StatCard label="Not Yet Arrived" value={(summary?.total ?? 0) - (summary?.checked_in ?? 0)} icon={Clock} color="purple" />
        <StatCard label="Online Attendees" value={summary?.alumni_online ?? 0} icon={Wifi} color="green" />
      </div>

      <div className="card">
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 18, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: '1 1 260px' }}>
            <Search size={15} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
            <input
              type="text"
              className="form-input"
              placeholder="Search name or email…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ width: '100%', paddingLeft: 36 }}
            />
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            {[['all', 'All'], ['checked', 'Checked In'], ['pending', 'Not Arrived']].map(([key, label]) => (
              <button
                key={key}
                className={`btn btn-sm ${filter === key ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => setFilter(key)}
              >
                {label}
              </button>
            ))}
          </div>
          {lastRefreshed && (
            <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginLeft: 'auto' }}>
              Updated {lastRefreshed.toLocaleTimeString()} · auto-refreshes every 15s
            </span>
          )}
        </div>

        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Type</th>
                <th>Mode</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 ? (
                <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 24 }}>
                  No registrations match.
                </td></tr>
              ) : visible.map(r => (
                <tr key={r.id}>
                  <td style={{ fontWeight: 500 }}>{r.full_name}</td>
                  <td style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{r.email}</td>
                  <td style={{ textTransform: 'capitalize' }}>{r.registrant_type}</td>
                  <td style={{ textTransform: 'capitalize' }}>{r.attendance_mode.replace('_', '-')}</td>
                  <td>
                    {r.checked_in ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--success)', fontSize: '0.8rem' }}>
                        <CheckCircle size={14} />
                        {r.checked_in_at ? new Date(r.checked_in_at).toLocaleTimeString() : 'Checked in'}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>—</span>
                    )}
                  </td>
                  <td>
                    {!r.checked_in && (
                      <button
                        className="btn btn-primary btn-sm"
                        disabled={checkingIn[r.id]}
                        onClick={() => handleCheckin(r)}
                      >
                        {checkingIn[r.id] ? '…' : 'Check In'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
